import * as React from 'react';

import {DropdownButton} from '../../button/dropdown-button/dropdown-button';
import {Stack} from '../../layout/stack/stack';
import type {StackSpacing} from '../../layout/stack/stack-legacy';

import {ScrollButton} from './scroll-button';

import styles from './scrollable-selection-container.module.css';

type Props<T> = {
    items: T[];
    renderItem: (item: T, index: number) => React.ReactElement;
    generateKey: (item: T, index: number) => string | number;
    initialSelectedIndex?: number;
    onSelect?: (index: number, ref: HTMLDivElement | null) => void;
    gap?: StackSpacing;
    headerProps?: {
        label: string;
        hasControls: boolean;
    };
    onLabelClick?: () => void;
    getLabelRef?: (ref: HTMLElement | null) => void;
};

/**
 * A carousel style container that allows for scrolling through a list of items,
 * while trying to keep the selected item centered in the container.
 */
export function ScrollableSelectionContainer<T>(props: Props<T>) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const itemRefs = React.useRef<Array<HTMLDivElement | null>>([]);
    const [canScrollLeft, setCanScrollLeft] = React.useState<boolean>(false);
    const [canScrollRight, setCanScrollRight] = React.useState<boolean>(false);

    const updateScrollButtons = React.useCallback(() => {
        const container = containerRef.current;
        if (container) {
            setCanScrollLeft(container.scrollLeft > 0);
            setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth);
        }
    }, []);

    const getScrollAmount = React.useCallback((): number => {
        const container = containerRef.current;
        if (!container) {
            return 0;
        }
        const firstItem = itemRefs.current[0];
        let itemWidth = firstItem ? firstItem.offsetWidth : 0;
        if (itemWidth === 0) {
            itemWidth = container.clientWidth; // fallback
        }
        const gapPx = props.gap || 0;

        return itemWidth + gapPx;
    }, [props.gap]);

    // Scroll to initially selected item on first render only
    const didInitialScrollRef = React.useRef<boolean>(false);
    React.useEffect(() => {
        if (didInitialScrollRef.current) {
            return;
        }
        const container = containerRef.current;
        const index =
            typeof props.initialSelectedIndex === 'number' ? props.initialSelectedIndex : null;
        if (container && index !== null && index >= 0 && index < itemRefs.current.length) {
            const selectedItem = itemRefs.current[index];
            if (selectedItem) {
                const containerRect = container.getBoundingClientRect();
                const itemRect = selectedItem.getBoundingClientRect();
                const scrollLeft =
                    selectedItem.offsetLeft - containerRect.width / 2 + itemRect.width / 2;
                container.scrollTo({left: scrollLeft, behavior: 'smooth'});
                didInitialScrollRef.current = true;
            }
        }
    }, [props.initialSelectedIndex, props.items.length]);

    React.useEffect(() => {
        const container = containerRef.current;
        if (container) {
            updateScrollButtons();
            container.addEventListener('scroll', updateScrollButtons);
            window.addEventListener('resize', updateScrollButtons);

            return () => {
                container.removeEventListener('scroll', updateScrollButtons);
                window.removeEventListener('resize', updateScrollButtons);
            };
        }
    }, [updateScrollButtons]);

    const handleItemClick = (index: number) => {
        const container = containerRef.current;
        const selectedItem = itemRefs.current[index];
        if (container && selectedItem) {
            const containerRect = container.getBoundingClientRect();
            const itemRect = selectedItem.getBoundingClientRect();
            const scrollLeft =
                selectedItem.offsetLeft - containerRect.width / 2 + itemRect.width / 2;
            container.scrollTo({left: scrollLeft, behavior: 'smooth'});
        }
        const ref = itemRefs.current[index];
        if (props.onSelect) {
            // @ts-expect-error
            props.onSelect(index, ref);
        }
    };

    const handlePrevious = React.useCallback(() => {
        const container = containerRef.current;
        if (container && canScrollLeft) {
            const amount = getScrollAmount();
            container.scrollBy({left: -amount, behavior: 'smooth'});
        }
    }, [canScrollLeft, getScrollAmount]);

    const handleNext = React.useCallback(() => {
        const container = containerRef.current;
        if (container && canScrollRight) {
            const amount = getScrollAmount();
            container.scrollBy({left: amount, behavior: 'smooth'});
        }
    }, [canScrollRight, getScrollAmount]);

    const shouldShowButtons =
        (canScrollLeft || canScrollRight) && props.headerProps && props.headerProps.hasControls;

    return (
        <Stack spacing={8}>
            {props.headerProps && (
                <Stack
                    isHorizontal={true}
                    justifyContent={props.headerProps.hasControls ? 'space-between' : 'end'}
                    spacing={4}
                    isFullWidth={true}
                >
                    <DropdownButton
                        label={props.headerProps.label}
                        onClick={props.onLabelClick}
                        getButtonRef={props.getLabelRef}
                    />
                    {shouldShowButtons && (
                        <Stack isHorizontal={true} spacing={4}>
                            <ScrollButton
                                direction='left'
                                onClick={handlePrevious}
                                disabled={!canScrollLeft}
                            />
                            <ScrollButton
                                direction='right'
                                onClick={handleNext}
                                disabled={!canScrollRight}
                            />
                        </Stack>
                    )}
                </Stack>
            )}
            <div
                ref={containerRef}
                style={{
                    gap: props.gap,
                    // Define the number of columns, each with a minimum width
                    // of 200px and a maximum width of 1fr. Forces all columns
                    // to be the same length.
                    // https://developer.mozilla.org/en-US/docs/Web/CSS/repeat
                    gridTemplateColumns: `repeat(${props.items.length}, minmax(200px, 1fr))`,
                }}
                className={styles['scrollable-selection-container']}
            >
                {props.items.map((item, index) => {
                    const key = props.generateKey(item, index);

                    return (
                        <div
                            key={key}
                            ref={(el) => {
                                itemRefs.current[index] = el;
                            }}
                            onClick={() => handleItemClick(index)}
                            className={styles['scrollable-selection-item']}
                        >
                            {props.renderItem(item, index)}
                        </div>
                    );
                })}
            </div>
        </Stack>
    );
}
